import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Grid,
  Divider,
  Button,
  TextField,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Device } from '../../../store/reducers/deviceReducers';
import { useState, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { updateTarget } from '../../../store/actions/deviceActions';
import deviceApi from '../../../api/devices';
import LocationPicker, { LocationPreview } from './LocationPicker';

interface EditTargetFormProps {
  device: Device;
}

const EditTargetForm = ({ device }: EditTargetFormProps) => {
  const dispatch = useDispatch();

  const handleChange = (key: string, value: string | number) => {
    dispatch(updateTarget({ serial: device.serial, target: { [key]: value } }));
  };

  const handleLocationChange = (latitude: number, longitude: number) => {
    dispatch(updateTarget({ serial: device.serial, target: { latitude, longitude } }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <Typography variant='body2' color='text.secondary'>
          Target ID
        </Typography>
        <Typography variant='body1' fontWeight='medium'>
          {device.target.id}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Typography variant='body2' color='text.secondary'>
          Type
        </Typography>
        <Typography variant='body1' fontWeight='medium' sx={{ textTransform: 'capitalize' }}>
          {device.target.type}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Typography variant='body2' color='text.secondary'>
          Name
        </Typography>
        <TextField
          fullWidth
          size='small'
          value={device.target.name}
          onChange={(e) => handleChange('name', e.target.value)}
          variant='outlined'
          margin='dense'
          type='text'
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size='small'
          value={device.target.latitude ?? ''}
          onChange={(e) => handleChange('latitude', e.target.value)}
          variant='outlined'
          margin='dense'
          type='number'
          label='Latitude'
          inputProps={{ step: 'any' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size='small'
          value={device.target.longitude ?? ''}
          onChange={(e) => handleChange('longitude', e.target.value)}
          variant='outlined'
          margin='dense'
          type='number'
          label='Longitude'
          inputProps={{ step: 'any' }}
        />
      </Grid>
      <Grid item xs={12}>
        <LocationPicker
          latitude={device.target.latitude}
          longitude={device.target.longitude}
          onChange={handleLocationChange}
        />
      </Grid>
    </Grid>
  );
};

const LocationField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body1' fontWeight='medium' sx={{ wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const DisplayTargetInfo = ({ device }: { device: Device }) => {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={3}
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <LocationPreview latitude={device.target.latitude} longitude={device.target.longitude} />
      <Grid container spacing={2.5} sx={{ flex: 1 }}>
        <Grid item xs={6} sm={4}>
          <LocationField label='Target ID' value={device.target.id} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <LocationField
            label='Type'
            value={
              <Box component='span' sx={{ textTransform: 'capitalize' }}>
                {device.target.type}
              </Box>
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocationField label='Name' value={device.target.name} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <LocationField label='Latitude' value={device.target.latitude} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <LocationField label='Longitude' value={device.target.longitude} />
        </Grid>
      </Grid>
    </Stack>
  );
};

const TargetInfo = ({ device }: { device: Device }) => {
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEdit = async () => {
    if (!edit) {
      setEdit(true);
      return;
    }
    setEdit(!edit);
    setLoading(true);
    await deviceApi.putTarget(device.target.id, {
      name: device.target.name,
      latitude: device.target.latitude,
      longitude: device.target.longitude,
    })();
    setLoading(false);
    setEdit(false);
  };

  const handleCancel = () => {
    setEdit(false);
  };

  return (
    <Card elevation={2} sx={{ borderRadius: 2, width: '100%' }}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <LocationOnIcon color='primary' />
              <Typography variant='h6'>Target Location</Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
              {edit && (
                <Button size='small' variant='outlined' onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
              <Button
                startIcon={edit ? <SaveIcon /> : <EditIcon />}
                size='small'
                variant='outlined'
                disabled={loading}
                onClick={() => handleEdit()}
              >
                {edit ? 'Save' : 'Edit'}
              </Button>
            </Stack>
          </Stack>
          <Divider />
          {edit ? <EditTargetForm device={device} /> : <DisplayTargetInfo device={device} />}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TargetInfo;
